import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getBrandsDetail } from 'services/cataloge';
import { IBrand } from 'types/category';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import './BrandDropdown.scss';

interface IBrandDropdown {
  value: string;
  categoryId: number | undefined;
  onChange: (id: number, name: string) => void;
}

/**
 * Selector de marca con filtro local, acoplado a la categoría activa.
 * Recarga las marcas disponibles cada vez que cambia el categoryId.
 * El dropdown se renderiza en un portal para escapar el overflow:hidden del contenedor padre.
 */
const BrandDropdown = ({ value, categoryId, onChange }: IBrandDropdown) => {
  const [searchString, setSearchString] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [brands, setBrands] = useState<IBrand[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef(value);

  const isFilled = !!value;
  const isDisabled = !categoryId || isLoading;

  const filteredBrands = brands.filter(b =>
    b.name?.toLowerCase().includes(searchString.toLowerCase())
  );

  /** Mantiene valueRef sincronizado para que handleClickOutside siempre lea el valor actual. */
  useEffect(() => {
    valueRef.current = value;
    setSearchString(value);
  }, [value]);

  /** Recarga las marcas cada vez que cambia la categoría seleccionada. */
  useEffect(() => {
    if (!categoryId) {
      setBrands([]);
      return;
    }
    setIsLoading(true);
    getBrandsDetail(categoryId)
      .then(data => setBrands(data.filter(b => b.id !== null && b.name !== null)))
      .catch(() => displayErrorNotification())
      .finally(() => setIsLoading(false));
  }, [categoryId]);

  /**
   * Cierra el dropdown al hacer click fuera. Se crea una sola vez (deps: []).
   * Lee valueRef.current en lugar de capturar value del closure para evitar stale state.
   */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const isInsideContainer = containerRef.current?.contains(e.target as Node);
      const isInsideDropdown = dropdownRef.current?.contains(e.target as Node);
      if (!isInsideContainer && !isInsideDropdown) {
        setIsOpen(false);
        setSearchString(valueRef.current);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Calcula la posición del dropdown midiendo el espacio disponible arriba y abajo del input.
   * Si hay más espacio arriba (o no cabe mínimamente abajo), abre hacia arriba (flip).
   */
  const updateDropdownPosition = () => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const maxHeight = 200;
    const minUsable = 120;

    if (spaceBelow >= minUsable || spaceBelow >= spaceAbove) {
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.min(maxHeight, spaceBelow),
        zIndex: 9999
      });
    } else {
      const availableHeight = Math.min(maxHeight, spaceAbove);
      setDropdownStyle({
        position: 'fixed',
        top: rect.top - availableHeight - 4,
        left: rect.left,
        width: rect.width,
        maxHeight: availableHeight,
        zIndex: 9999
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value);
    setIsOpen(true);
  };

  const handleFocus = () => {
    if (isDisabled) return;
    updateDropdownPosition();
    setIsOpen(true);
  };

  const handleSelect = (brand: IBrand) => {
    setSearchString(brand.name!);
    setIsOpen(false);
    onChange(brand.id!, brand.name!);
  };

  return (
    <div className={`brandDropdown${isFilled ? ' filled' : ''}`} ref={containerRef}>
      <label className="fieldLabel">Marca*</label>
      <div className="inputWrapper">
        <input
          ref={inputRef}
          value={searchString}
          placeholder={isLoading ? 'Cargando...' : 'Selecciona la marca'}
          disabled={isDisabled}
          onChange={handleInputChange}
          onFocus={handleFocus}
          autoComplete="off"
        />
      </div>
      {isOpen && !isDisabled && createPortal(
        <div className="brandDropdownList" style={dropdownStyle} ref={dropdownRef}>
          {filteredBrands.length === 0 && (
            <div className="dropdownItem empty">Sin resultados</div>
          )}
          {filteredBrands.map(brand => (
            <div
              key={brand.id!}
              className="dropdownItem"
              onMouseDown={() => handleSelect(brand)}
            >
              {brand.name}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default BrandDropdown;
